import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';

import { reportEvent } from '~/domains/analytics';
import { useGetBranding } from '~/domains/auth/hooks/useGetBranding';
import { forgotPassword } from '~/services/identity/unauthenticated/identity.service.api';
import { useShowSplashNotification } from '~/domains/common/hooks/useShowSplashNotification';

import { useRecaptcha } from './useRecaptcha';

const useForgotPassword = (isReset: boolean) => {
  const { t } = useTranslation('auth');
  const { showSplashNotification } = useShowSplashNotification();

  const { getRecaptchaToken } = useRecaptcha();

  const { destination } = useGetBranding();
  const {
    mutate: handleForgotPassword,
    isLoading,
    isSuccess: isLinkSent,
  } = useMutation({
    mutationKey: ['forgotPassword'],
    mutationFn: async ({ email }: { email: string }) => {
      const eventName = isReset ? 'User > Reset Password' : 'User > Forgot Password';
      reportEvent(eventName, {
        email,
        brand: destination,
      });

      const token = await getRecaptchaToken();
      await forgotPassword({
        email: email.trim(),
        token,
        destination,
      });
    },
    onError: (error: AxiosError & { response?: { data?: { showSupportCTA?: boolean } } }) => {
      // @ts-expect-error: message exists on data object
      const errorMessage = error?.response?.data?.message;
      const errorStatus = error.response?.status;
      const showSupportCTA = error.response?.data?.showSupportCTA;
      showSplashNotification({
        title: t('forgotPassword.errorTitle'),
        message: errorStatus !== 401 ? errorMessage : t('forgotPassword.errorMessage'),
        color: 'red',
        autoClose: 8000,
        showSupportCTA,
      });

      const eventName = isReset
        ? 'User > Reset Password - Error'
        : 'User > Forgot Password - Error';
      reportEvent(eventName, {
        errorMessage,
        errorStatus,
      });
    },
  });

  return {
    handleForgotPassword,
    isLoading,
    isLinkSent,
  };
};

export default useForgotPassword;
